import React, { useState } from 'react';
import { 
  Plus, 
  Upload, 
  Settings, 
  Clock, 
  Cpu, 
  HardDrive, 
  Play, 
  AlertCircle, 
  CheckCircle,
  Zap,
  Globe,
  Database,
  Monitor,
  Brain,
  Microscope,
  Layers,
  Target,
  Code,
  Server,
  ChevronRight,
  Sparkles,
  Star,
  Save,
  RotateCcw,
  X,
  FileText,
  Calendar,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { JobFormData } from './types';

const SubmitJob: React.FC = () => {
  const [formData, setFormData] = useState<JobFormData>({
    jobName: '',
    masterId: '',
    slurmCommand: '',
    selectedApp: '',
    priority: 'normal',
    maxRuntime: '24',
    cpuCores: '1',
    memory: '1',
    storage: '10',
    envVars: [{ key: '', value: '' }],
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // 预定义的应用模板
  const appTemplates = [
    {
      id: 'pytorch',
      name: 'PyTorch 训练',
      icon: Brain,
      description: '深度学习模型训练',
      gradient: 'from-orange-500 to-red-500',
      command: 'python train.py --epochs 100 --batch-size 32',
      envVars: [
        { key: 'CUDA_VISIBLE_DEVICES', value: '0' },
        { key: 'PYTORCH_CUDA_ALLOC_CONF', value: 'max_split_size_mb:128' }
      ]
    },
    {
      id: 'tensorflow',
      name: 'TensorFlow 训练',
      icon: Target,
      description: '机器学习模型训练',
      gradient: 'from-blue-500 to-indigo-500',
      command: 'python train_tf.py --model resnet50 --dataset imagenet',
      envVars: [
        { key: 'TF_CPP_MIN_LOG_LEVEL', value: '2' },
        { key: 'CUDA_VISIBLE_DEVICES', value: '0' }
      ]
    },
    {
      id: 'molecular',
      name: '分子动力学',
      icon: Microscope,
      description: 'GROMACS 分子模拟',
      gradient: 'from-green-500 to-emerald-500',
      command: 'gmx mdrun -deffnm md_0_1 -v',
      envVars: [
        { key: 'GMX_MAXBACKUP', value: '-1' },
        { key: 'OMP_NUM_THREADS', value: '8' }
      ]
    },
    {
      id: 'hpc',
      name: 'HPC 计算',
      icon: Server,
      description: '高性能计算任务',
      gradient: 'from-purple-500 to-pink-500',
      command: 'mpirun -np 16 ./simulation --input config.in',
      envVars: [
        { key: 'MPI_TYPE_DEPTH', value: '16' },
        { key: 'OMP_NUM_THREADS', value: '4' }
      ]
    },
    {
      id: 'bioinformatics',
      name: '生物信息学',
      icon: Database,
      description: '基因组学数据分析',
      gradient: 'from-teal-500 to-cyan-500',
      command: 'python analyze_genome.py --input sample.fastq --output results/',
      envVars: [
        { key: 'NUMBA_NUM_THREADS', value: '8' },
        { key: 'OPENBLAS_NUM_THREADS', value: '1' }
      ]
    },
    {
      id: 'custom',
      name: '自定义任务',
      icon: Code,
      description: '自定义计算任务',
      gradient: 'from-gray-500 to-slate-500',
      command: '',
      envVars: []
    }
  ];

  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template.id);
    setFormData(prev => ({
      ...prev,
      selectedApp: template.id,
      slurmCommand: template.command,
      envVars: template.envVars.length > 0 ? template.envVars : [{ key: '', value: '' }]
    }));
  };

  const addEnvVar = () => {
    setFormData(prev => ({
      ...prev,
      envVars: [...prev.envVars, { key: '', value: '' }]
    }));
  };

  const removeEnvVar = (index: number) => {
    setFormData(prev => ({
      ...prev,
      envVars: prev.envVars.filter((_, i) => i !== index)
    }));
  };

  const updateEnvVar = (index: number, field: 'key' | 'value', value: string) => {
    setFormData(prev => ({
      ...prev,
      envVars: prev.envVars.map((env, i) => 
        i === index ? { ...env, [field]: value } : env
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.jobName || !formData.slurmCommand) {
      toast.error('请填写必要的作业信息');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 模拟提交API调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('作业提交成功！');
      
      // 重置表单
      setFormData({
        jobName: '',
        masterId: '',
        slurmCommand: '',
        selectedApp: '',
        priority: 'normal',
        maxRuntime: '24',
        cpuCores: '1',
        memory: '1',
        storage: '10',
        envVars: [{ key: '', value: '' }],
        description: ''
      });
      setSelectedTemplate('');
    } catch (error) {
      toast.error('作业提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      jobName: '',
      masterId: '',
      slurmCommand: '',
      selectedApp: '',
      priority: 'normal',
      maxRuntime: '24',
      cpuCores: '1',
      memory: '1',
      storage: '10',
      envVars: [{ key: '', value: '' }],
      description: ''
    });
    setSelectedTemplate('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 portrait:p-4">
      {/* 顶部标题区 */}
      <div className="max-w-7xl mx-auto mb-8 portrait:mb-5">
        <div className="flex items-center justify-between portrait:flex-col portrait:items-start portrait:space-y-3">
          <div className="flex items-center space-x-5 portrait:space-x-4">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 portrait:w-12 portrait:h-12 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Plus className="w-7 h-7 portrait:w-6 portrait:h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 portrait:w-4 portrait:h-4 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                <Play className="w-2.5 h-2.5 portrait:w-2 portrait:h-2 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent leading-tight">
                提交作业
              </h1>
              <p className="text-gray-600 mt-2 portrait:mt-1 text-base portrait:text-sm leading-relaxed">创建和配置您的计算作业</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 portrait:space-x-2">
            <button
              onClick={resetForm}
              className="flex items-center space-x-2 portrait:space-x-1 px-4 py-2 portrait:px-3 portrait:py-1.5 text-gray-600 hover:text-gray-800 hover:bg-white/50 rounded-lg transition-all duration-200 text-sm portrait:text-xs"
            >
              <RotateCcw className="w-4 h-4 portrait:w-3 portrait:h-3" />
              <span>重置</span>
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 portrait:gap-4">
          {/* 左侧：模板选择 */}
          <div className="lg:col-span-1 space-y-6 portrait:space-y-4">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <h2 className="text-lg portrait:text-base font-semibold text-gray-900 mb-4 portrait:mb-3 flex items-center">
                <Layers className="w-5 h-5 portrait:w-4 portrait:h-4 mr-2 portrait:mr-1.5 text-blue-600" />
                选择模板
              </h2>
              
              <div className="space-y-3 portrait:space-y-2">
                {appTemplates.map((template) => {
                  const IconComponent = template.icon;
                  const isSelected = selectedTemplate === template.id;
                  
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleTemplateSelect(template)}
                      className={`w-full p-4 portrait:p-3 rounded-xl portrait:rounded-lg border transition-all duration-300 text-left group ${
                        isSelected
                          ? 'border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg scale-105 portrait:scale-100'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md hover:scale-102 portrait:hover:scale-100'
                      }`}
                    >
                      <div className="flex items-start space-x-3 portrait:space-x-2">
                        <div className={`p-2 portrait:p-1.5 rounded-lg portrait:rounded-md bg-gradient-to-r ${template.gradient} text-white`}>
                          <IconComponent className="w-5 h-5 portrait:w-4 portrait:h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium portrait:text-sm text-gray-900 group-hover:text-blue-600 transition-colors">
                            {template.name}
                          </h3>
                          <p className="text-sm portrait:text-xs text-gray-600 mt-1 portrait:mt-0.5">
                            {template.description}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 portrait:w-4 portrait:h-4 text-blue-600 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 中间：基本信息 */}
          <div className="lg:col-span-2 space-y-6 portrait:space-y-4">
            {/* 基本信息 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <h2 className="text-lg portrait:text-base font-semibold text-gray-900 mb-6 portrait:mb-4 flex items-center">
                <FileText className="w-5 h-5 portrait:w-4 portrait:h-4 mr-2 portrait:mr-1.5 text-blue-600" />
                基本信息
              </h2>
              
              <div className="space-y-6 portrait:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 portrait:grid-cols-1 gap-4 portrait:gap-3">
                  <div>
                    <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                      作业名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.jobName}
                      onChange={(e) => setFormData(prev => ({ ...prev, jobName: e.target.value }))}
                      className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                      placeholder="输入作业名称"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                      Master ID
                    </label>
                    <input
                      type="text"
                      value={formData.masterId}
                      onChange={(e) => setFormData(prev => ({ ...prev, masterId: e.target.value }))}
                      className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                      placeholder="可选的Master ID"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    SLURM 命令 *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.slurmCommand}
                    onChange={(e) => setFormData(prev => ({ ...prev, slurmCommand: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-mono text-sm portrait:text-xs"
                    placeholder="输入要执行的命令，例如：python train.py --epochs 100"
                  />
                </div>

                <div>
                  <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    作业描述
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                    placeholder="可选的作业描述"
                  />
                </div>
              </div>
            </div>

            {/* 资源配置 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <h2 className="text-lg portrait:text-base font-semibold text-gray-900 mb-6 portrait:mb-4 flex items-center">
                <Settings className="w-5 h-5 portrait:w-4 portrait:h-4 mr-2 portrait:mr-1.5 text-blue-600" />
                资源配置
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 portrait:grid-cols-1 gap-6 portrait:gap-4">
                <div>
                  <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    优先级
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                  >
                    <option value="low">低</option>
                    <option value="normal">普通</option>
                    <option value="high">高</option>
                    <option value="urgent">紧急</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    最大运行时间 (小时)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={formData.maxRuntime}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxRuntime: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    <Cpu className="w-4 h-4 portrait:w-3 portrait:h-3 mr-1 portrait:mr-0.5" />
                    CPU 核心数
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="128"
                    value={formData.cpuCores}
                    onChange={(e) => setFormData(prev => ({ ...prev, cpuCores: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    <HardDrive className="w-4 h-4 portrait:w-3 portrait:h-3 mr-1 portrait:mr-0.5" />
                    内存 (GB)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1024"
                    value={formData.memory}
                    onChange={(e) => setFormData(prev => ({ ...prev, memory: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                  />
                </div>

                <div className="md:col-span-2 portrait:col-span-1">
                  <label className="flex items-center text-sm portrait:text-xs font-medium text-gray-700 mb-2 portrait:mb-1.5">
                    <Database className="w-4 h-4 portrait:w-3 portrait:h-3 mr-1 portrait:mr-0.5" />
                    存储空间 (GB)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10240"
                    value={formData.storage}
                    onChange={(e) => setFormData(prev => ({ ...prev, storage: e.target.value }))}
                    className="w-full px-4 py-3 portrait:px-3 portrait:py-2 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                  />
                </div>
              </div>
            </div>

            {/* 环境变量 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <div className="flex items-center justify-between mb-6 portrait:mb-4">
                <h2 className="text-lg portrait:text-base font-semibold text-gray-900 flex items-center">
                  <Globe className="w-5 h-5 portrait:w-4 portrait:h-4 mr-2 portrait:mr-1.5 text-blue-600" />
                  环境变量
                </h2>
                <button
                  type="button"
                  onClick={addEnvVar}
                  className="flex items-center space-x-2 portrait:space-x-1 px-3 py-2 portrait:px-2 portrait:py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg portrait:rounded-md transition-all duration-200 text-sm portrait:text-xs"
                >
                  <Plus className="w-4 h-4 portrait:w-3 portrait:h-3" />
                  <span>添加</span>
                </button>
              </div>
              
              <div className="space-y-3 portrait:space-y-2">
                {formData.envVars.map((env, index) => (
                  <div key={index} className="flex items-center space-x-3 portrait:space-x-2 p-3 portrait:p-2 bg-gray-50 rounded-lg portrait:rounded-md">
                    <input
                      type="text"
                      value={env.key}
                      onChange={(e) => updateEnvVar(index, 'key', e.target.value)}
                      className="flex-1 px-3 py-2 portrait:px-2 portrait:py-1.5 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                      placeholder="变量名"
                    />
                    <span className="text-gray-400 text-sm portrait:text-xs">=</span>
                    <input
                      type="text"
                      value={env.value}
                      onChange={(e) => updateEnvVar(index, 'value', e.target.value)}
                      className="flex-1 px-3 py-2 portrait:px-2 portrait:py-1.5 border border-gray-200 rounded-lg portrait:rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm portrait:text-xs"
                      placeholder="变量值"
                    />
                    {formData.envVars.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEnvVar(index)}
                        className="p-2 portrait:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg portrait:rounded-md transition-all duration-200"
                      >
                        <X className="w-4 h-4 portrait:w-3 portrait:h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧：预览和提交 */}
          <div className="lg:col-span-1 space-y-6 portrait:space-y-4">
            {/* 作业预览 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <h3 className="text-lg portrait:text-base font-semibold text-gray-900 mb-4 portrait:mb-3 flex items-center">
                <Monitor className="w-5 h-5 portrait:w-4 portrait:h-4 mr-2 portrait:mr-1.5 text-blue-600" />
                作业预览
              </h3>
              
              <div className="space-y-3 portrait:space-y-2 text-sm portrait:text-xs">
                <div className="flex justify-between py-2 portrait:py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">作业名称:</span>
                  <span className="font-medium text-gray-900 text-right truncate ml-2">{formData.jobName || '-'}</span>
                </div>
                <div className="flex justify-between py-2 portrait:py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">CPU核心:</span>
                  <span className="font-medium text-gray-900">{formData.cpuCores}</span>
                </div>
                <div className="flex justify-between py-2 portrait:py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">内存:</span>
                  <span className="font-medium text-gray-900">{formData.memory} GB</span>
                </div>
                <div className="flex justify-between py-2 portrait:py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">存储:</span>
                  <span className="font-medium text-gray-900">{formData.storage} GB</span>
                </div>
                <div className="flex justify-between py-2 portrait:py-1.5 border-b border-gray-100">
                  <span className="text-gray-600">运行时间:</span>
                  <span className="font-medium text-gray-900">{formData.maxRuntime}h</span>
                </div>
                <div className="flex justify-between py-2 portrait:py-1.5">
                  <span className="text-gray-600">优先级:</span>
                  <span className={`font-medium px-2 py-1 portrait:px-1.5 portrait:py-0.5 rounded text-xs portrait:text-[10px] ${
                    formData.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                    formData.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    formData.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {formData.priority === 'urgent' ? '紧急' :
                     formData.priority === 'high' ? '高' :
                     formData.priority === 'normal' ? '普通' : '低'}
                  </span>
                </div>
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-xl p-6 portrait:p-4">
              <button
                type="submit"
                disabled={isSubmitting || !formData.jobName || !formData.slurmCommand}
                className={`w-full flex items-center justify-center space-x-3 portrait:space-x-2 py-4 portrait:py-3 px-6 portrait:px-4 rounded-xl portrait:rounded-lg font-semibold text-white transition-all duration-300 text-base portrait:text-sm ${
                  isSubmitting || !formData.jobName || !formData.slurmCommand
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 portrait:hover:scale-100 shadow-lg hover:shadow-xl'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 portrait:w-4 portrait:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>提交中...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 portrait:w-4 portrait:h-4" />
                    <span>提交作业</span>
                  </>
                )}
              </button>
            </div>

            {/* 帮助信息 */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl portrait:rounded-xl border border-blue-200 p-6 portrait:p-4">
              <div className="flex items-start space-x-3 portrait:space-x-2">
                <Info className="w-5 h-5 portrait:w-4 portrait:h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2 portrait:mb-1.5 text-base portrait:text-sm">提示</h4>
                  <ul className="text-sm portrait:text-xs text-blue-800 space-y-1 portrait:space-y-0.5">
                    <li>• 选择合适的模板可以快速配置作业</li>
                    <li>• 确保命令路径和参数正确</li>
                    <li>• 合理配置资源避免浪费</li>
                    <li>• 环境变量可用于配置运行环境</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SubmitJob;
